import { useQuery } from '@tanstack/react-query'
import bbox from '@turf/bbox'
import type { MultiPolygon } from 'geojson'
import { useEffect, useMemo, useState } from 'react'
import type { LayerProps } from 'react-map-gl'
import { Layer, Source, useMap } from 'react-map-gl'
import { aDrawMode, aDrawnFeature, aMapStyle, useAtomValue } from '~/atoms'
import { REGRID_TOKEN } from '~/constants'
import type { Client, Parcel, Properties } from '~/models'
import { MobilePopup } from '../components'
import MapParcelPopup from './parcel-popup'

type ParcelLayerProps = {
  client: Client
}

type Coordinates = {
  lat: number
  lon: number
}

type ParcelClickEvent = (mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) & {
  features?: mapboxgl.MapboxGeoJSONFeature[] | undefined
} & mapboxgl.EventData

export default function ParcelLayer({ client }: ParcelLayerProps) {
  const mapRef = useMap()
  const mapType = useAtomValue(aMapStyle)
  const [selectedParcel, setSelectedParcel] = useState<Parcel | null>(null)
  const [selectedParcelId, setSelectedParcelId] = useState<string | null>(null)
  const [selectedParcelIndex, setSelectedParcelIndex] = useState<number | null>(
    null
  )
  const drawMode = useAtomValue(aDrawMode)
  const drawFeature = useAtomValue(aDrawnFeature)

  const [popupCoordinates, setPopupCoordinates] = useState<Coordinates>({
    lat: 0,
    lon: 0,
  })

  const parcelQuery = useQuery({
    queryKey: ['parcel', selectedParcel?.ll_uuid],
    queryFn: async () => {
      const response = await fetch(
        `https://app.regrid.com/api/v2/parcels/point?lon=${popupCoordinates.lon}&lat=${popupCoordinates.lat}&token=${REGRID_TOKEN}`
      )
      const result = await response.json()
      return result
    },
    enabled: !!selectedParcel?.ll_uuid,
  })

  const maxBounds = useMemo(() => {
    if (!client.geom) {
      return
    }
    return bbox(client.geom as MultiPolygon)
  }, [])

  const parcels: Properties[] = useMemo(() => {
    if (parcelQuery?.data?.parcels?.features?.length > 0) {
      setSelectedParcelIndex(0)

      return parcelQuery.data.parcels.features.map((feature: any) => {
        return feature.properties as Properties
      })
    }

    return []
  }, [parcelQuery.data])

  const selectedParcelData = useMemo(() => {
    if (selectedParcelIndex === null) return null
    return parcels[selectedParcelIndex] ?? null
  }, [parcels, selectedParcelIndex])

  function onPopupClose() {
    if (mapRef.current! && selectedParcelId) {
      mapRef.current!.setFeatureState(
        {
          source: 'parcel-data',
          sourceLayer: 'parcels',
          id: selectedParcelId,
        },
        { selected: false }
      )
    }
    setSelectedParcel(null)
  }

  useEffect(() => {
    if (mapRef.current && !!client.geom && !drawMode) {
      const map = mapRef.current?.getMap()
      let hoveredPolygonId: string | null = null
      let isListingClicked = false
      if (!map) {
        return
      }

      let touchStartTime: number | null = null
      let touchStartPosition: { x: number; y: number } | null = null

      function onMouseMove(e: ParcelClickEvent) {
        isListingClicked = false

        if ((e.features || []).length > 0) {
          if (hoveredPolygonId !== null) {
            mapRef.current!.getMap().getCanvasContainer().style.cursor = 'grab'
            map.setFeatureState(
              {
                source: 'parcel-data',
                sourceLayer: 'parcels',
                id: hoveredPolygonId,
              },
              { hover: false }
            )
          }
          hoveredPolygonId = e.features![0].id as string
          mapRef.current!.getMap().getCanvasContainer().style.cursor = 'pointer'
          map.setFeatureState(
            {
              source: 'parcel-data',
              sourceLayer: 'parcels',
              id: hoveredPolygonId,
            },
            { hover: true }
          )
        }
      }

      function onParcelClick(e: ParcelClickEvent) {
        if (!isListingClicked && (e.features || []).length > 0) {
          const clickedFeature = e.features![0]
          const clickedId = clickedFeature.id as string

          setPopupCoordinates({
            lat: e.lngLat.lat,
            lon: e.lngLat.lng,
          })

          setSelectedParcel(clickedFeature.properties as Properties)

          if (selectedParcelId) {
            map.setFeatureState(
              {
                source: 'parcel-data',
                sourceLayer: 'parcels',
                id: selectedParcelId,
              },
              { selected: false }
            )
          }

          setSelectedParcelId(clickedId)

          map.setFeatureState(
            { source: 'parcel-data', sourceLayer: 'parcels', id: clickedId },
            { selected: true }
          )
        }
        isListingClicked = false
      }

      function onTouchStart(e: mapboxgl.MapTouchEvent) {
        touchStartTime = Date.now()
        touchStartPosition = { x: e.point.x, y: e.point.y }
      }

      function onTouchEnd(e: ParcelClickEvent) {
        if (touchStartTime === null || touchStartPosition === null) return

        const touchEndTime = Date.now()
        const touchEndPosition = { x: e.point.x, y: e.point.y }

        const touchDistanceX = Math.abs(
          touchStartPosition.x - touchEndPosition.x
        )
        const touchDistanceY = Math.abs(
          touchStartPosition.y - touchEndPosition.y
        )
        const touchDuration = touchEndTime - touchStartTime

        const maxDistance = 10
        const maxDuration = 300

        const isClick =
          touchDuration < maxDuration &&
          touchDistanceX < maxDistance &&
          touchDistanceY < maxDistance

        touchStartTime = null
        touchStartPosition = null

        if (isClick) {
          onParcelClick(e)
        } else {
          onMouseMove(e)
        }

        if (hoveredPolygonId !== null) {
          map.setFeatureState(
            {
              source: 'parcel-data',
              sourceLayer: 'parcels',
              id: hoveredPolygonId,
            },
            { hover: false }
          )
        }
      }

      const onPropertyClick = (e: ParcelClickEvent) => {
        if (e.features && e.features.length > 0) {
          isListingClicked = true
          setSelectedParcel(null)
        } else {
          isListingClicked = false
        }
      }

      map.on('click', 'property-single-clusters', onPropertyClick)
      map.on('touchstart', 'property-single-clusters', onPropertyClick)
      map.on('click', 'parcels-fills', onParcelClick)
      map.on('touchstart', 'parcels-fills', onTouchStart)
      map.on('touchend', 'parcels-fills', onTouchEnd)
      map.on('mousemove', 'parcels-fills', onMouseMove)

      map.on('mouseleave', 'parcels-fills', () => {
        isListingClicked = false
        if (hoveredPolygonId !== null) {
          mapRef.current!.getMap().getCanvasContainer().style.cursor = 'grab'
          map.setFeatureState(
            {
              source: 'parcel-data',
              sourceLayer: 'parcels',
              id: hoveredPolygonId,
            },
            { hover: false }
          )
        }
        hoveredPolygonId = null
      })
    }
  }, [mapRef.current, selectedParcelId])

  const layerStyle = useMemo(() => {
    const style: LayerProps = {
      id: 'parcels',
      'source-layer': 'parcels',
      filter: ['!=', ['get', 'land_use_code'], 'R'],
      type: 'line',
      // interactive: true,
      paint: {
        'line-color': mapType === 'street' ? '#000' : '#fff',
        'line-opacity': mapType === 'street' ? 0.15 : 0.5,
      },
      layout: {},
    }

    return style
  }, [mapType])

  const layerStyleFill: LayerProps = {
    id: 'parcels-fills',
    type: 'fill',
    'source-layer': 'parcels',
    layout: {},
    paint: {
      'fill-color': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        '#0284c7',
        ['boolean', ['feature-state', 'hover'], false],
        '#0284c7',
        '#000000',
      ],
      'fill-opacity': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        0.5,
        ['boolean', ['feature-state', 'hover'], false],
        0.25,
        0,
      ],
    },
    filter: ['!=', ['get', 'land_use_code'], 'R'],
  }

  if (!client.geom || !!drawMode || !!drawFeature) {
    return null
  }

  return (
    <>
      <Source
        id="parcel-data"
        type="vector"
        url={`https://tiles.regrid.com/api/v1/parcels?token=${REGRID_TOKEN}&format=mvt`}
        minzoom={13}
        bounds={maxBounds}
        promoteId="ll_uuid">
        <Layer
          beforeId="z-index-3"
          id="parcel-style-fill"
          {...layerStyleFill}
        />
        <Layer beforeId="z-index-3" {...layerStyle} />

        {selectedParcel && (
          <MobilePopup
            className="show-close !m-0 w-full select-text !p-0"
            maxWidth="400px"
            key={selectedParcel.ll_uuid}
            latitude={popupCoordinates.lat}
            longitude={popupCoordinates.lon}
            closeButton={true}
            closeOnMove={true}
            closeOnClick={false}
            onClose={onPopupClose}
            style={{ zIndex: 48 }}>
            <MapParcelPopup
              parcels={parcels}
              selectedParcelData={selectedParcelData}
              selectedParcelIndex={selectedParcelIndex}
              setSelectedParcelIndex={setSelectedParcelIndex}
            />
          </MobilePopup>
        )}
      </Source>
    </>
  )
}
